import React, { ChangeEvent, useEffect, useRef } from "react";

import _ from "lodash";
import cx from "classnames";
import { nanoid } from "nanoid";

import { SectionBarProps, SectionState } from "../../../types";

import { AutoGrowInput } from "../../../../../components/input";
import { useDndMonitor } from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";

import styles from "./sections.module.scss";
import { ScrollableContainer } from "src/components/scroller";

const SectionBar: React.FC<SectionBarProps> = ({
  collection,
  sections,
  selected,
  menuState,
  onMenuChange,
  onSelectionChange,
  onRename,
  onRemove,
  onMove,
}) => {
  const idRef = useRef(nanoid(6));

  const editing = menuState.primaryAction === "edit";

  useDndMonitor({
    onDragOver(event) {
      const { active, over } = event;
      const sourceContainer = _.get(
        active,
        "data.current.sortable.containerId",
      );
      const section = _.get(active, "data.current.section");
      const target = _.get(over, "data.current.section");

      if (idRef.current === sourceContainer && section) {
        if (target && section.index !== target.index) {
          onMove(section.index, target.index);
        }
      }
    },
  });

  const handleSelection = (section: SectionState) => {
    if (onSelectionChange) {
      onSelectionChange(section.index);
    }
  };

  const handleRemoveSection = (section: SectionState) => {
    if (onRemove) {
      onRemove(section.index);
    }
  };

  const handleRename = (section: SectionState, value: string) => {
    if (onRename) {
      onRename(section.index, value);
    }
  };

  return (
    <SortableContext
      id={idRef.current}
      items={sections.map((section) => section.id || "new-section")}
      strategy={rectSortingStrategy}
    >
      <div className={cx(styles.content, { [styles.editing]: editing })}>
        <div className={styles.sectionsList}>
          <ScrollableContainer
            distance={300}
            leftArrowComponent={LeftArrow}
            rightArrowComponent={RightArrow}
            showInitialAnimation={true}
          >
            {sections.map((section, index) => {
              return (
                <SectionItem
                  section={section}
                  editing={editing}
                  key={section.id}
                  selected={selected === index}
                  onClick={handleSelection}
                  onNameChange={handleRename}
                  onRemove={handleRemoveSection}
                />
              );
            })}
          </ScrollableContainer>
        </div>
      </div>
    </SortableContext>
  );
};

interface SectionItemProps {
  section: SectionState;
  editing: boolean;
  selected: boolean;
  // eslint-disable-next-line no-unused-vars
  onClick: (section: SectionState) => void;
  // eslint-disable-next-line no-unused-vars
  onRemove: (section: SectionState) => void;
  // eslint-disable-next-line no-unused-vars
  onNameChange: (section: SectionState, value: string) => void;
}

const SectionItem: React.FC<SectionItemProps> = ({
  section,
  selected,
  editing,
  onClick,
  onNameChange,
  onRemove,
}) => {
  const [originalName, setOriginalName] = React.useState(section.name);

  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: section.uid || nanoid(6),
    disabled: !editing,
    data: {
      section: section,
      type: "section",
      accepts: ["section", "asset"],
    },
  });

  useEffect(() => {
    setOriginalName(section.name);
  }, [editing]);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    onNameChange(section, e.target.value);
  };

  const handleRemove = () => {
    onRemove(section);
  };

  return (
    <div
      className={cx(styles.section, "collection_section", {
        [styles.selected]: selected,
        [styles.editing]: editing,
        [styles.dragging]: isDragging,
      })}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-section-index={section.index}
      onClick={() => onClick(section)}
    >
      <AutoGrowInput
        readOnly={!(editing && selected)}
        className={styles.input}
        placeholder={originalName}
        value={section.name}
        onChange={handleNameChange}
      />

      {editing && selected && (
        <button className={styles.remove} onClick={handleRemove}>
          <i className="paperflite-close"></i>
        </button>
      )}
    </div>
  );
};

const DraggingSectionCard: React.FC<{ section: SectionState }> = ({
  section,
}) => {
  return (
    <div
      className={cx(styles.section, styles.dragPreview, "collection_section")}
    >
      {section.name}
    </div>
  );
};

enum ScrollArrowPosition {
  "Left",
  "Right",
}

interface ArrowProps {
  position: ScrollArrowPosition
  scrollAvailable: boolean;
}

const Arrow: React.FC<ArrowProps> = ({ position, scrollAvailable }) => {
  return (
    <div className={cx(styles.arrow, {
      [styles.left]: position === ScrollArrowPosition.Left,
      [styles.right]: position === ScrollArrowPosition.Right,
      [styles.scrollAvailable]: scrollAvailable,
    })}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const LeftArrow: React.FC<any> = ({ scrollAvailable }) => {
  return (
    <Arrow position={ScrollArrowPosition.Left} scrollAvailable={scrollAvailable} />
  );
};

const RightArrow: React.FC<any> = ({ scrollAvailable }) => {
  return (
    <Arrow position={ScrollArrowPosition.Right} scrollAvailable={scrollAvailable} />
  );
};

export { SectionBar, DraggingSectionCard };
